import { useCallback, useMemo, useRef, useState } from "react";
import { Button, Col, Divider, Form, FormInstance, notification, Popconfirm, Row, Typography } from "antd";
import { useSelector } from "react-redux";

import { IConfiguracaoContabil, ISolicitacaoAtivacao } from "../../../../../interfaces";
import { ICreateConfiguracaoSistemaContabil, IDeleteConfiguracaoSistemaContabil, IUpdateConfiguracaoSistemaContabil } from "./interfaces";
import { stateGlobal } from "../../../../../../../../interfaces/Global";

import { EmitirErro } from "../../../../../../../../services/EmitirErro";
import { mask, unMask } from "../../../../../../../../utils/MascaraDeCampos";
import InputConfiguracaoContabil, { IInputConfiguracaoContabilCustomChangeParams } from "./InputConfiguracaoContabil";

interface IProps {
    solicitacao: ISolicitacaoAtivacao;
    onConfiguracaoSistemaContabilChange: (configuracao: IConfiguracaoContabil | null) => void;
    onCreateConfiguracaoSistemaContabil: (data: ICreateConfiguracaoSistemaContabil) => Promise<IConfiguracaoContabil | null>;
    onUpdateConfiguracaoSistemaContabil: (data: IUpdateConfiguracaoSistemaContabil) => Promise<IConfiguracaoContabil | null>;
    onDeleteConfiguracaoSistemaContabil: (data: IDeleteConfiguracaoSistemaContabil) => Promise<void>;
}

export default function ConfigurarSistemaContabil({
    solicitacao,
    onConfiguracaoSistemaContabilChange,
    onCreateConfiguracaoSistemaContabil,
    onUpdateConfiguracaoSistemaContabil,
    onDeleteConfiguracaoSistemaContabil,
}: IProps) {
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const propsState = useSelector((state: stateGlobal) => state.global);

    const formRef = useRef<FormInstance<any>>(null);

    const handleClickDeletarConfiguracao = useCallback(async () => {
        try {
            setIsDeleting(true);

            await onDeleteConfiguracaoSistemaContabil({ solicitacaoId: solicitacao.id });

            const values: { [key: string]: string | number | null | boolean } = {};

            solicitacao.sistemaContabil.atributosConfiguracao.forEach((e) => {
                values[e.nomeNorm] = null;
            });

            formRef.current?.setFieldsValue((values));

            setIsDeleting(false);
            onConfiguracaoSistemaContabilChange(null);

            notification.success({
                message: 'Sucesso',
                description:
                    'Parâmetros de configuração deletados com sucesso!',
            });
        } catch (error) {
            setIsDeleting(false);
            EmitirErro(error, true, "deleteConfiguracaoContabil_aosdjasndas0dhasd");
        }

    }, [solicitacao.id]);

    const handleCustomInputChange = useCallback(({
        field,
        value,
        validate
    }: IInputConfiguracaoContabilCustomChangeParams) => {

        formRef.current?.setFieldsValue(
            { [field]: value }
        );

        if (validate) {
            formRef.current?.validateFields([field])
        }
    }, []);

    const initialValues = useMemo(() => {
        if (!solicitacao.configuracaoContabil) {
            return;
        }

        const values: { [key: string]: string | number | null | boolean | number[] } = {};

        solicitacao.sistemaContabil.atributosConfiguracao.forEach((e) => {
            const value = solicitacao.configuracaoContabil![e.nomeNorm as keyof IConfiguracaoContabil];

            values[e.nomeNorm] = e.nomeNorm.toLowerCase().includes('cnpj') ? mask(value || '', ["99.999.999/9999-99"]) : value;
        });

        return values;
    }, [solicitacao]);

    const handleSubmit = useCallback(async () => {

        try {
            formRef.current?.validateFields().then(async (values: any) => {
                try {
                    const formattedValues: { [key: string]: any } = {};

                    Object.entries(values).forEach(([key, value]: [key: string, value: any]) => {
                        if (key.toLowerCase().includes('cnpj')) {
                            formattedValues[key] = unMask(value);
                            return;
                        }

                        formattedValues[key] = value;
                    });

                    if (!solicitacao.configuracaoContabil) {
                        setIsSubmiting(true);

                        const createdConfiguracaoContabil = await onCreateConfiguracaoSistemaContabil({
                            solicitacaoId: solicitacao.id,
                            configuracao: formattedValues,
                        });

                        onConfiguracaoSistemaContabilChange(createdConfiguracaoContabil);
                        setIsSubmiting(false);
                        notification.success({
                            message: 'Sucesso',
                            description:
                                'Parâmetros de configuração cadastrados com sucesso!',
                        });

                        return;
                    }

                    setIsSubmiting(true);

                    const updatedConfiguracaoContabil = await onUpdateConfiguracaoSistemaContabil({
                        solicitacaoId: solicitacao.id,
                        configuracao: formattedValues,
                    });

                    onConfiguracaoSistemaContabilChange(updatedConfiguracaoContabil);
                    setIsSubmiting(false);
                    notification.success({
                        message: 'Sucesso',
                        description:
                            'Parâmetros de configuração atualizados com sucesso!',
                    });

                } catch (error) {
                    setIsSubmiting(false);
                    EmitirErro(error, true, "createParametrosConfiguracaoSistemaContabil_aosdjasndas0dhasd");
                }
            })
                .catch(() => {
                    setIsSubmiting(false);
                    notification.error({
                        message: 'Erro',
                        description:
                            'Preencha os campos obrigatorios!',
                    });
                });;

        } catch (error) {
            setIsSubmiting(false);
            EmitirErro(error);
        }
    }, [solicitacao]);

    return (
        <>
            <div
                style={{
                    border: `1px solid ${propsState.theme === 'light' ? '#0000000f' : '#ffffff1f'}`,
                    borderRadius: 8,
                    padding: '12px 0',
                    marginTop: 32
                }}
            >
                <div
                    style={{
                        padding: '0 16px',
                    }}
                >
                    <Typography.Title level={4} style={{ margin: 0 }}>Parâmetros de configuração</Typography.Title>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div
                    style={{
                        padding: '0 16px',
                    }}
                >
                    <Form
                        ref={formRef}
                        name="formConfiguracaoSistemaContabil"
                        layout="vertical"
                        onFinishFailed={() => { }}
                        initialValues={initialValues}
                    >
                        <Row gutter={24}>
                            {
                                solicitacao.sistemaContabil.atributosConfiguracao.map((atributo) => (
                                    <InputConfiguracaoContabil
                                        key={atributo.nomeNorm}
                                        atributo={atributo}
                                        onCustomChange={handleCustomInputChange}
                                        initialValues={initialValues}
                                    />
                                ))
                            }
                        </Row>

                        <Divider style={{ margin: 0, marginBottom: 24 }} />

                        <Row gutter={[10, 10]} justify={"end"} >
                            <Col>
                                {
                                    solicitacao.configuracaoContabil && (
                                        <Popconfirm
                                            title="Deseja realmente deletar a configuração?"
                                            onConfirm={handleClickDeletarConfiguracao}
                                            onCancel={() => { }}
                                            okText="Sim"
                                            cancelText="Não"
                                            disabled={isDeleting || isSubmiting}
                                            okButtonProps={{
                                                loading: isDeleting,
                                                disabled: isDeleting,
                                            }}
                                            cancelButtonProps={{
                                                disabled: isDeleting
                                            }}
                                        >
                                            <Button
                                                danger
                                                disabled={isDeleting || isSubmiting}
                                                loading={isDeleting}
                                            >
                                                Deletar configuração
                                            </Button>
                                        </Popconfirm>
                                    )
                                }
                            </Col>

                            <Col>
                                <Button
                                    type="primary"
                                    onClick={handleSubmit}
                                    disabled={isSubmiting || isDeleting}
                                    loading={isSubmiting}
                                >
                                    Salvar configuração
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </div>
        </>
    )
}
